import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Typography from '@ui/Typography';
import TypographyLink from '@ui/TypographyLink';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import type { LegacyProjectBannerPhase } from '../legacyProjectRetirement';

interface LegacyProjectBannerProps {
  phase: LegacyProjectBannerPhase;
  learnMoreHref: string;
  onGetStartedClick: () => void;
  isWellSaidAdmin?: boolean;
}

interface BannerCopy {
  title: string;
  description: string;
  phase: LegacyProjectBannerPhase;
}

const BANNER_COPY_BY_PHASE: Record<LegacyProjectBannerPhase, BannerCopy> = {
  preViewOnlyAnnouncement: {
    title: 'This is a legacy project',
    description:
      'WellSaid Studio is our new experience. Convert this project to keep editing with the latest voices and features.',
    phase: 'preViewOnlyAnnouncement',
  },
  viewOnlyWarning: {
    title: 'This is a legacy project',
    description:
      'Legacy projects become view-only on July 30. Convert this project to keep editing with the latest voices and features.',
    phase: 'viewOnlyWarning',
  },
  viewOnly: {
    title: 'View only',
    description:
      'This project is view-only. Convert to WellSaid Studio to keep editing.',
    phase: 'viewOnly',
  },
};

const WELL_SAID_ADMIN_VIEW_ONLY_COPY: BannerCopy = {
  title: 'This is a legacy project',
  description:
    'This project is view-only for customers. As a WellSaid admin, you can still edit. Convert to WellSaid Studio when ready.',
  phase: 'viewOnly',
};

const useStyles = makeStyles({ name: 'LegacyProjectBanner' })(theme => ({
  root: {
    backgroundColor: palette.gray_20,
    // note: left padding is different than right padding for layout reasons in terms of icon alignment
    padding: theme.spacing(1, 2, 1, 1),
  },
  warningIcon: {
    color: palette.papaya_400,
    flexShrink: 0,
  },
  title: {
    marginRight: theme.spacing(1),
  },
  cta: {
    backgroundColor: palette.gray_500,
    color: palette.gray_0,
    whiteSpace: 'nowrap',
    minWidth: 108,
    '&:hover': {
      backgroundColor: palette.gray_400,
    },
    '&:focus': {
      outline: `2px solid ${palette.gray_400}`,
      backgroundColor: palette.gray_400,
    },
  },
  learnMore: {
    marginLeft: theme.spacing(0.5),
  },
}));

const IconBell = ({ phase }: { phase: LegacyProjectBannerPhase }) => {
  let bgcolor: string = 'transparent';
  if (phase === 'preViewOnlyAnnouncement') {
    bgcolor = 'transparent';
  } else if (phase === 'viewOnlyWarning') {
    bgcolor = palette.papaya_300;
  } else if (phase === 'viewOnly') {
    bgcolor = palette.punch_300;
  }
  return (
    <Box
      height={24}
      width={24}
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor={bgcolor}
      borderRadius="50%"
    >
      <Icon type="bell" style={{ marginRight: -3 }} color="secondary" />
    </Box>
  );
};

export default function LegacyProjectBanner({
  phase,
  learnMoreHref,
  onGetStartedClick,
  isWellSaidAdmin = false,
}: LegacyProjectBannerProps) {
  const { classes } = useStyles();
  const {
    title,
    description,
    phase: bannerPhase,
  } = phase === 'viewOnly' && isWellSaidAdmin
    ? WELL_SAID_ADMIN_VIEW_ONLY_COPY
    : BANNER_COPY_BY_PHASE[phase];

  return (
    <Box className={classes.root} component="aside">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Box display="flex" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            rowGap={0.5}
            flexWrap="nowrap"
          >
            <Box paddingRight={1.5} paddingTop={0.5}>
              <IconBell phase={bannerPhase} />
            </Box>
            <Box gap={1.5}>
              <Typography
                variant="h4"
                bold
                className={classes.title}
                color="gray_1000"
                inline
              >
                {title}
              </Typography>
              <Typography variant="pMd" color="gray_700" inline>
                {description}
              </Typography>
              <TypographyLink
                href={learnMoreHref}
                target="_blank"
                rel="noopener noreferrer"
                variant="pMd"
                underlined
                className={classes.learnMore}
                color="gray_700"
              >
                Learn more
              </TypographyLink>
            </Box>
          </Box>
        </Box>
        <Button
          variant="overlay"
          size="small"
          className={classes.cta}
          onClick={onGetStartedClick}
          data-e2e="legacy-project-banner-get-started-action"
        >
          Get started
        </Button>
      </Box>
    </Box>
  );
}
